import { useState, useCallback } from 'react';
import { captureSessionMessage } from 'src/utils/sentry/sessionSentry';

const SESSION_EXPIRED_STORAGE_KEY = 'session_expired';

/**
 * Check if session was marked as expired in sessionStorage.
 * Callable from non-React code.
 */
export function isSessionExpiredInStorage(): boolean {
  try {
    return sessionStorage.getItem(SESSION_EXPIRED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Clear the session expired flag from sessionStorage.
 * Callable from non-React code (e.g. after triggering logout on refresh).
 */
export function clearSessionExpiredFlag(): void {
  try {
    sessionStorage.removeItem(SESSION_EXPIRED_STORAGE_KEY);
  } catch {
    // ignore — sessionStorage may be unavailable
  }
}

export interface UseSessionExpiredOverlayReturn {
  isSessionExpiredVisible: boolean;
  showSessionExpired: () => void;
  hideSessionExpired: (context?: 'refresh') => void;
  sessionExpiredMessage: string | undefined;
  setSessionExpiredMessage: (message: string | undefined) => void;
}

/**
 * Hook to manage session expired overlay state.
 * Persists the "expired" flag in sessionStorage so that a page refresh
 * cannot bypass the session-expired state.
 */
export function useSessionExpiredOverlay(): UseSessionExpiredOverlayReturn {
  const [isSessionExpiredVisible, setIsSessionExpiredVisible] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<
    string | undefined
  >();

  const showSessionExpired = useCallback(() => {
    console.log('🚨 useSessionExpiredOverlay: Showing session expired overlay');
    captureSessionMessage('Showing session expired overlay', 'warning', {
      event: 'session.expired.overlay.shown',
    });
    setIsSessionExpiredVisible(true);
    try {
      sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, 'true');
    } catch {
      // ignore — sessionStorage may be unavailable
    }
  }, []);

  const hideSessionExpired = useCallback((context?: 'refresh') => {
    const msg =
      context === 'refresh'
        ? '✅ useSessionExpiredOverlay: Hiding session expired overlay after successful token refresh'
        : '✅ useSessionExpiredOverlay: Hiding session expired overlay';
    console.log(msg);
    captureSessionMessage('Hiding session expired overlay', 'info', {
      event: 'session.expired.overlay.hidden',
      extra: { context: context ?? null },
    });
    setIsSessionExpiredVisible(false);
    setSessionExpiredMessage(undefined);
    clearSessionExpiredFlag();
  }, []);

  return {
    isSessionExpiredVisible,
    showSessionExpired,
    hideSessionExpired,
    sessionExpiredMessage,
    setSessionExpiredMessage,
  };
}
